import React, { useCallback, useMemo } from 'react';
import ICON_COMMON from 'utils/icons.common';
import styled from 'styled-components';
import BWAccordion from 'components/shared/BWAccordion';
import FormInput from 'components/shared/BWFormControl/FormInput';
import DataTable from 'components/shared/DataTable';
import PropTypes from 'prop-types';

import { useFormContext, useFieldArray } from 'react-hook-form';
import { showConfirmModal } from 'actions/global';
import { useDispatch } from 'react-redux';
import { Segmented } from 'antd';
import { PromotionnalSchema } from 'pages/Coupon/utils/contructor';
import FormNumber from 'components/shared/BWFormControl/FormNumber';
import { showToast } from 'utils/helpers';
import { TYPE_PROMOTION_VALUE } from 'pages/Coupon/utils/constants';
import { typePromotionValues } from 'pages/Coupon/utils/helpers';
import {
  getValueWatchByIndex,
  handleGetTotal,
  checkAndGetValuePromational,
  checkCodePromotionalList,
} from './mathCoupon';
import utilVar from '../../helpers/index';
import WrapUnregister from 'components/shared/FormCommon/WrapUnregister';
import { formatIntegerInput, parseIntegerInput } from 'pages/Coupon/utils/numberFormat';
import BWButton from 'components/shared/BWButton';

const SegmentedStyled = styled(Segmented)`
  .ant-segmented-item-selected {
    background-color: #1b3c40 !important;
    color: white !important;
  }
`;

const CouponModeGuide = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 12px;
  padding: 11px 14px;
  border: 1px solid #b9dcff;
  border-radius: 10px;
  background: #f0f7ff;
  color: #344054;
  font-size: 13px;
  line-height: 1.5;

  .bw_coupon_guide_icon {
    display: inline-flex;
    flex: 0 0 22px;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #2e90fa;
    color: #fff;
    font-weight: 700;
  }

  strong {
    color: #101828;
  }

  .bw_coupon_guide_content {
    flex: 1;
    min-width: 0;
  }

  .bw_coupon_guide_action {
    flex: 0 0 auto;
    white-space: nowrap;
  }
`;

const FieldWithUnit = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  > *:first-child {
    min-width: 0;
    flex: 1;
  }

  .bw_coupon_unit {
    flex: 0 0 auto;
    color: #475467;
    font-weight: 600;
  }
`;

const RangeFields = styled.div`
  display: grid;
  grid-template-columns: minmax(96px, 1fr) 16px minmax(96px, 1fr);
  gap: 6px;
  align-items: center;

  .bw_coupon_range_separator {
    color: #98a2b3;
    font-weight: 700;
    text-align: center;
  }
`;

const CouponPromotional = ({ loading, title, disabled, isReady, couponId }) => {
  const methods = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'promotional_list',
  });
  const dispatch = useDispatch();

  const funcCheckvalue = (value, objArr, func) => {
    let result = value;
    if (!value && value !== 0) {
      result = func(objArr);
    }
    return result;
  };
  const columns = [
    {
      header: 'Mã khuyến mại',
      classNameHeader: 'bw_text_center',
      styleHeader: { minWidth: '180px' },
      style: { minWidth: '180px' },
      formatter: (_, index) => {
        return (
          <React.Fragment>
            <FormInput
              style={{
                padding: '5px',
                border: '1px solid #ccc',
                borderRadius: '6px',
              }}
              disabled={disabled}
              type='text'
              field={`promotional_list.${index}.coupon_code`}
              placeholder='Mã khuyến mại'
              validation={{
                required: 'Mã khuyến mại là bắt buộc',
              }}
              onChange={(e) => {
                methods.clearErrors(`promotional_list.${index}.coupon_code`);
                const promotional_list = getValueWatchByIndex(methods, null, 'promotional_list');
                let valueUppercase = e.target.value.toUpperCase();
                let valueReturn = checkCodePromotionalList(valueUppercase, index, promotional_list);
                methods.setValue(`promotional_list.${index}.coupon_code`, valueReturn);
              }}
            />
          </React.Fragment>
        );
      },
    },
    {
      header: 'Tỷ lệ ngân sách',
      classNameHeader: 'bw_text_center',
      styleHeader: { minWidth: '120px' },
      style: { minWidth: '120px' },
      formatter: (_, index) => {
        const promotional_list = getValueWatchByIndex(methods, null, 'promotional_list');
        const totalMax = utilVar.getValueReturnInArray(100, promotional_list, 'percent_value', index);

        return (
          <div
            style={{
              display: 'flex',
              justifyItems: 'center',
              alignItems: 'center',
            }}>
            <FormNumber
              style={{
                width: '80px',
              }}
              bordered={true}
              min={0}
              max={totalMax}
              field={`promotional_list.${index}.percent_value`}
              onChange={(e) => {
                checkAndGetValuePromational(methods, null, index, 'percent_value', e);
              }}
              validation={{
                required: 'Tỷ lệ ngân sách là bắt buộc',
              }}
              disabled={disabled}
            />
            <b>%</b>
          </div>
        );
      },
    },
    {
      header: 'Ngân sách phân bổ',
      classNameHeader: 'bw_text_center',
      styleHeader: { minWidth: '160px' },
      style: { minWidth: '160px' },
      formatter: (_, index) => {
        let totalValue = getValueWatchByIndex(methods, index, 'total_value', 'promotional_list');
        let totalPercent = getValueWatchByIndex(methods, index, 'percent_value', 'promotional_list');
        let totalBudget = getValueWatchByIndex(methods, null, 'budget');
        const getTotalValueByIndex = funcCheckvalue(totalValue, [totalPercent, totalBudget], handleGetTotal);
        return (
          <FieldWithUnit>
            <FormNumber
              type='text'
              min={0}
              precision={0}
              formatter={formatIntegerInput}
              parser={parseIntegerInput}
              field={`promotional_list.${index}.total_value`}
              value={getTotalValueByIndex}
              disabled={true}
            />
            <span className='bw_coupon_unit'>đ</span>
          </FieldWithUnit>
        );
      },
    },
    {
      header: 'Mức giảm',
      classNameHeader: 'bw_text_center',
      styleHeader: { minWidth: '210px' },
      style: { minWidth: '210px' },
      formatter: (_, index) => {
        const codeType = Number(methods.watch(`promotional_list.${index}.code_type`));
        return (
          <FieldWithUnit>
            <FormNumber
              bordered={true}
              min={0}
              max={codeType === TYPE_PROMOTION_VALUE.PERCENT ? 100 : undefined}
              precision={0}
              formatter={codeType === TYPE_PROMOTION_VALUE.MONEY ? formatIntegerInput : undefined}
              parser={parseIntegerInput}
              field={`promotional_list.${index}.code_value`}
              onChange={(e) => {
                checkAndGetValuePromational(methods, null, index, 'code_value', e);
              }}
              validation={{
                required: 'Giá trị khuyến mại là bắt buộc',
              }}
              disabled={disabled || !getValueWatchByIndex(methods, index, 'percent_value', 'promotional_list')}
            />
            <SegmentedStyled
              disabled={disabled}
              value={methods.watch(`promotional_list.${index}.code_type`)}
              options={typePromotionValues}
              onChange={(e) => {
                checkAndGetValuePromational(methods, null, index, 'code_type', e);
              }}
            />
          </FieldWithUnit>
        );
      },
    },
    {
      header: 'Lượt sử dụng',
      classNameHeader: 'bw_text_center',
      styleHeader: { minWidth: '130px' },
      style: { minWidth: '130px' },
      formatter: (_, index) => {
        return (
          <React.Fragment>
            <FormNumber
              bordered={true}
              min={0}
              precision={0}
              formatter={formatIntegerInput}
              parser={parseIntegerInput}
              field={`promotional_list.${index}.quantity`}
              validation={{
                required: 'Số lượt sử dụng là bắt buộc',
              }}
              onChange={(e) => {
                checkAndGetValuePromational(methods, null, index, 'quantity', e);
              }}
              disabled={disabled}
            />
          </React.Fragment>
        );
      },
    },
    {
      header: 'Giá trị đơn hàng áp dụng',
      classNameHeader: 'bw_text_center',
      styleHeader: { minWidth: '260px' },
      style: { minWidth: '260px' },
      formatter: (_, index) => {
        return (
          <RangeFields>
            <FormNumber
              disabled={disabled}
              min={0}
              precision={0}
              formatter={formatIntegerInput}
              parser={parseIntegerInput}
              field={`promotional_list.${index}.min_total_money`}
              placeholder='Từ'
            />
            <div className='bw_coupon_range_separator'>→</div>
            <FormNumber
              placeholder='Đến'
              disabled={disabled}
              type='text'
              min={0}
              precision={0}
              formatter={formatIntegerInput}
              parser={parseIntegerInput}
              field={`promotional_list.${index}.max_total_money`}
            />
          </RangeFields>
        );
      },
    },
    {
      header: 'Số sản phẩm áp dụng',
      classNameHeader: 'bw_text_center',
      styleHeader: { minWidth: '230px' },
      style: { minWidth: '230px' },
      formatter: (_, index) => {
        return (
          <RangeFields>
            <FormNumber
              disabled={disabled}
              min={0}
              precision={0}
              formatter={formatIntegerInput}
              parser={parseIntegerInput}
              field={`promotional_list.${index}.min_count`}
              placeholder='Từ'
            />
            <div className='bw_coupon_range_separator'>→</div>
            <FormNumber
              placeholder='Đến'
              disabled={disabled}
              type='text'
              min={0}
              precision={0}
              formatter={formatIntegerInput}
              parser={parseIntegerInput}
              field={`promotional_list.${index}.max_count`}
            />
          </RangeFields>
        );
      },
    },
    {
      header: 'Giảm tối đa',
      classNameHeader: 'bw_text_center',
      styleHeader: { minWidth: '160px' },
      style: { minWidth: '160px' },
      hidden: !(methods.watch('promotional_list') ?? []).some(
        (item) => Number(item?.code_type) === TYPE_PROMOTION_VALUE.PERCENT,
      ),
      formatter: (_, index) => {
        const maxValue = getValueWatchByIndex(methods, index, 'total_value', 'promotional_list');
        return (
          getValueWatchByIndex(methods, index, 'code_type', 'promotional_list') * 1 ===
            TYPE_PROMOTION_VALUE.PERCENT && (
            <FormNumber
              type='text'
              style={{
                border: '1px solid #ccc',
                borderRadius: '6px',
              }}
              min={0}
              max={maxValue}
              precision={0}
              formatter={formatIntegerInput}
              parser={parseIntegerInput}
              disabled={disabled || methods.watch(`promotional_list.${index}.code_type`) === TYPE_PROMOTION_VALUE.MONEY}
              field={`promotional_list.${index}.max_value_reduce`}
              placeholder='Giá trị giảm tối đa'
            />
          )
        );
      },
    },
  ];

  const isCheckTotalCoupon = Boolean(methods.watch('budget'));

  const promotionalRatioValue = (methods.watch('promotional_list') ?? []).reduce(
    (acc, obj) => acc + Number(obj?.percent_value || 0),
    0,
  );

  const handleAddSharedCoupon = useCallback(() => {
    if (!isCheckTotalCoupon) {
      showToast.error('Vui lòng nhập ngân sách coupon!', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
      return;
    }

    if (promotionalRatioValue >= 100) {
      showToast.error('Tỷ lệ khuyến mại đã bằng 100%', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
      return;
    }

    append(new PromotionnalSchema(TYPE_PROMOTION_VALUE.MONEY));
  }, [append, isCheckTotalCoupon, promotionalRatioValue]);

  const actions = useMemo(() => {
    return [
      {
        icon: ICON_COMMON.trash,
        hidden: disabled,
        color: 'red',
        onClick: (value, index) => {
          dispatch(
            showConfirmModal([`Xoá mã khuyến mại này?`], () => {
              remove(index);
            }),
          );
        },
      },
    ];
  }, [disabled, dispatch, remove]);

  return (
    <React.Fragment>
      {(isReady && couponId) || (!isReady && !couponId) ? (
        <WrapUnregister field={['promotional_list']}>
          <BWAccordion isRequired title={title}>
            <CouponModeGuide>
              <span className='bw_coupon_guide_icon'>i</span>
              <div className='bw_coupon_guide_content'>
                <strong>Mã dùng chung:</strong> mỗi dòng là một mã khuyến mại; trường <strong>Lượt sử dụng</strong> quy
                định mã đó được dùng tối đa bao nhiêu lần.
                <br />
                Muốn hệ thống tạo nhiều mã riêng biệt, chọn <strong>“Hệ thống tự sinh nhiều mã riêng biệt”</strong>{' '}
                trong phần Thông tin mã khuyến mại.
              </div>
              {!disabled && (
                <BWButton
                  className='bw_coupon_guide_action'
                  type='success'
                  icon={ICON_COMMON.add}
                  content='Thêm mã dùng chung'
                  showContent
                  onClick={handleAddSharedCoupon}
                />
              )}
            </CouponModeGuide>
            <DataTable
              style={{
                marginTop: '0px',
              }}
              hiddenActionRow
              noPaging
              noSelect
              data={fields}
              columns={columns}
              loading={loading}
              actions={actions}
            />
          </BWAccordion>
        </WrapUnregister>
      ) : null}
    </React.Fragment>
  );
};

CouponPromotional.propTypes = {
  title: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  couponId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CouponPromotional;
